import React from "react";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { cn } from "@/lib/utils";

export function fmtCurrency(v) {
  if (v == null) return "—";
  const n = Number(v);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n.toFixed(0)}`;
}

export function fmtValue(card) {
  if (card.format === "currency") return fmtCurrency(card.value);
  if (card.format === "percent") return `${card.value}%`;
  return Number(card.value || 0).toLocaleString("en-IN");
}

export function PageHeader({ title, description, children, breadcrumb }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
      <div>
        {breadcrumb && <div className="text-xs text-slate-400 mb-1.5">{breadcrumb}</div>}
        <h1 className="font-display font-bold text-2xl sm:text-[28px] text-slate-900 tracking-tight">{title}</h1>
        {description && <p className="text-sm text-slate-500 mt-1">{description}</p>}
      </div>
      {children && <div className="flex items-center gap-2 shrink-0">{children}</div>}
    </div>
  );
}

export function KpiCard({ card, index = 0, onClick }) {
  const Icon = Icons[card.icon] || Icons.Circle;
  const up = (card.trend || 0) >= 0;
  return (
    <motion.button initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.03 }}
      onClick={onClick} data-testid={`kpi-${card.key}`}
      className={cn("vs-card p-4 text-left w-full", onClick && "vs-card-hover cursor-pointer")}>
      <div className="flex items-center justify-between">
        <div className="h-9 w-9 rounded-lg bg-vs-light flex items-center justify-center"><Icon className="h-[18px] w-[18px] text-vs-royal" /></div>
        {card.trend != null && (
          <span className={cn("inline-flex items-center gap-0.5 text-[11px] font-semibold", up ? "text-emerald-600" : "text-rose-500")}>
            {up ? <Icons.TrendingUp className="h-3 w-3" /> : <Icons.TrendingDown className="h-3 w-3" />}{Math.abs(card.trend)}%
          </span>
        )}
      </div>
      <div className="mt-3 font-display font-extrabold text-2xl text-slate-900 tracking-tight">{fmtValue(card)}</div>
      <div className="text-xs text-slate-500 mt-0.5">{card.label}</div>
    </motion.button>
  );
}

export function Card({ title, subtitle, children, className, action }) {
  return (
    <div className={cn("vs-card p-5", className)}>
      {(title || action) && (
        <div className="flex items-center justify-between mb-4">
          <div>{title && <h3 className="font-display font-semibold text-slate-900">{title}</h3>}
            {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}</div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

export function EmptyState({ icon = "Inbox", title, description, action }) {
  const Icon = Icons[icon] || Icons.Inbox;
  return (
    <div className="text-center py-16 px-4">
      <div className="h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4"><Icon className="h-7 w-7 text-slate-400" /></div>
      <h3 className="font-display font-semibold text-slate-800">{title}</h3>
      {description && <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function SkeletonRows({ rows = 5 }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-12 rounded-lg bg-slate-100 animate-pulse" />
      ))}
    </div>
  );
}

export function Funnel({ stages }) {
  const max = Math.max(...stages.map((s) => s.value), 1);
  const allZero = stages.every((s) => !s.value);
  if (allZero) return <EmptyState icon="BarChart3" title="Not enough data yet" description="Pipeline analytics will appear once submissions are recorded." />;
  const colors = ["#1D4ED8", "#2563EB", "#3B82F6", "#60A5FA", "#818CF8", "#A78BFA", "#C084FC", "#E879F9"];
  return (
    <div className="space-y-2.5">
      {stages.map((s, i) => (
        <div key={s.label} className="flex items-center gap-3">
          <div className="w-28 text-xs text-slate-600 shrink-0">{s.label}</div>
          <div className="flex-1 h-8 bg-slate-50 rounded-lg overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: `${Math.max((s.value / max) * 100, 4)}%` }} transition={{ delay: i * 0.05, duration: 0.5 }}
              className="h-full rounded-lg flex items-center px-3" style={{ background: colors[i % colors.length] }}>
              <span className="text-xs font-semibold text-white">{s.value}</span>
            </motion.div>
          </div>
        </div>
      ))}
    </div>
  );
}
